// Definimos la URL base según la variable de entorno, con fallback a localhost
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export const fetchWashingCycles = async ({ limit, page, query }) => {
  try {
    const response = await fetch(`${API_URL}/washing-cycles/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ limit, page, query }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener los ciclos de lavado');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error al obtener los ciclos de lavado:', error);
    throw error;
  }
};

/**
 * Lista SOLO los ciclos de lavado abortados.
 *
 * Endpoint dedicado (`POST /washing-cycles/aborted/filter`) que
 * internamente fuerza `abortado: true` en el query. Se usa desde la
 * tabla de ciclos abortados y desde el modal de registro, de modo
 * que su consumo sea independiente del listado general y no se
 * mezclen accidentalmente ciclos normales con abortados.
 *
 * El `query` que recibe NO debe incluir `abortado`: el backend lo
 * agrega por defecto.
 */
export const fetchAbortedWashingCycles = async ({ limit, page, query = {} } = {}) => {
  try {
    const response = await fetch(`${API_URL}/washing-cycles/aborted/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ limit, page, query }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener los ciclos de lavado abortados');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error al obtener los ciclos de lavado abortados:', error);
    throw error;
  }
};

// Reportes: totales de ciclos de lavado
export const getWashingCycleReportTotals = async ({ query = {} }) => {
  try {

    if (query?.createdAt) {
      query.fecha = query.createdAt;
      delete query.createdAt;
    }
    const response = await fetch(`${API_URL}/washing-cycles/reports/totals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    });
    if (!response.ok) {
      throw new Error('Error al obtener totales de ciclos de lavado');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error al obtener totales de ciclos de lavado:', error);
    throw error;
  }
};

// Reportes: series temporales (día/semana/mes) de kilos lavados y conteo
export const getWashingCycleDaily = async ({ query = {}, groupBy = 'day' }) => {
  try {
    const response = await fetch(`${API_URL}/washing-cycles/reports/daily`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, groupBy }),
    });
    if (!response.ok) {
      throw new Error('Error al obtener serie diaria de ciclos de lavado');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error al obtener serie diaria de ciclos de lavado:', error);
    throw error;
  }
};

export const createWashingCycle = async (cycleData) => {
  try {
    const response = await fetch(`${API_URL}/washing-cycles/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cycleData),
    });

    if (!response.ok) {
      throw new Error('Error al crear el registro de ciclo de lavado');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error al crear el registro de ciclo de lavado:', error);
    throw error;
  }
};

/**
 * Registra un ciclo de lavado ABORTADO.
 *
 * A diferencia de `createWashingCycle`, este endpoint dedicado NO
 * descuenta un día a la fecha. Se usa desde el modal de ciclos
 * abortados, donde el operador registra el ciclo el mismo día en que
 * ocurre (no al día siguiente como en el flujo normal de cierre de
 * turno). Usar el endpoint equivocado aquí causaba que la fecha
 * quedara guardada con un día de menos.
 */
export const createAbortedWashingCycle = async (cycleData) => {
  try {
    const response = await fetch(`${API_URL}/washing-cycles/create-aborted`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cycleData),
    });

    if (!response.ok) {
      throw new Error('Error al crear el registro de ciclo de lavado abortado');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error al crear el registro de ciclo de lavado abortado:', error);
    throw error;
  }
};

export const updateWashingCycle = async (cycleId, cycleData) => {
  try {
    const response = await fetch(`${API_URL}/washing-cycles/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: cycleId, data: cycleData }),
    });

    if (!response.ok) {
      throw new Error('Error al actualizar el registro de ciclo de lavado');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error al actualizar el registro de ciclo de lavado:', error);
    throw error;
  }
};

export const deleteWashingCycle = async (cycleId) => {
  try {
    const response = await fetch(`${API_URL}/washing-cycles/delete/${cycleId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Error al eliminar el registro de ciclo de lavado');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error al eliminar el registro de ciclo de lavado:', error);
    throw error;
  }
};

export const exportWashingCycles = async (query = {}) => {
  try {
    const qs = encodeURIComponent(JSON.stringify(query || {}));
    const response = await fetch(`${API_URL}/washing-cycles/export?query=${qs}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Error al exportar los ciclos de lavado');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error al exportar los ciclos de lavado:', error);
    throw error;
  }
};

export const getTotalWashedKilos = async (idLavanderia, dateRange) => {
  try {
    const response = await fetch(`${API_URL}/washing-cycles/total-kilos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        idLavanderia,
        startDate: dateRange?.$gte,
        endDate: dateRange?.$lte
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener los kilos lavados');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error al obtener los kilos lavados:', error);
    throw error;
  }
};

// Reportes: participación por lavadora (agregación en frontend)
// Devuelve { data: nominal[], aborted: aborted[], totals: { totalKilos, totalCiclos } }
// - `data` contiene SOLO ciclos nominales (kilosLavados reales, ciclos no abortados)
// - `aborted` contiene SOLO ciclos abortados (kilosLavados y ciclos abortados)
export const getWashingParticipationByMachine = async ({ query = {}, limit = 10000, page = 1 } = {}) => {
  try {
    // 1) Intentar endpoint dedicado del backend
    const resp = await fetch(`${API_URL}/washing-cycles/reports/participation-by-machine`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    });
    if (resp.ok) {
      const payload = await resp.json();
      // Soportar tanto el formato nuevo (data + aborted) como el viejo (solo data)
      const data = Array.isArray(payload?.data?.nominal)
        ? payload.data.nominal
        : (Array.isArray(payload?.data) ? payload.data : []);
      const aborted = Array.isArray(payload?.data?.aborted)
        ? payload.data.aborted
        : [];
      const totals = payload?.extra || { totalKilos: 0, totalCiclos: 0 };
      return { data, aborted, totals };
    }
    // 2) Fallback a agregación en frontend
    throw new Error('Falling back to local aggregation');
  } catch (err) {
    try {
      const resp = await fetchWashingCycles({ limit, page, query });
      const items = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);

      const buildAgg = (filterFn) => {
        const map = new Map();
        let totalKilos = 0;
        let totalCiclos = 0;
        for (const it of items) {
          if (!filterFn(it)) continue;
          const id = it?.maquina?.id || it?.maquina?._id || 'sin_id';
          const nombre = it?.maquina?.nombre || 'Sin máquina';
          const kilos = Number(it?.kilosLavados || it?.kilos || 0);
          const ciclos = Number(it?.ciclos || 0);
          totalKilos += kilos;
          totalCiclos += ciclos;
          if (!map.has(id)) map.set(id, { id, nombre, kilosLavados: 0, ciclos: 0 });
          const cur = map.get(id);
          cur.kilosLavados += kilos;
          cur.ciclos += ciclos;
          map.set(id, cur);
        }
        const data = Array.from(map.values())
          .map(m => ({
            ...m,
            pctKilos: totalKilos > 0 ? (m.kilosLavados / totalKilos) * 100 : 0,
            pctCiclos: totalCiclos > 0 ? (m.ciclos / totalCiclos) * 100 : 0,
          }))
          .sort((a, b) => b.ciclos - a.ciclos);
        return { data, totalKilos, totalCiclos };
      };

      // Nominales: ciclos NO abortados
      const nominalAgg = buildAgg((it) => !it?.abortado);
      // Abortados: ciclos abortados
      const abortedAgg = buildAgg((it) => !!it?.abortado);

      return {
        data: nominalAgg.data,
        aborted: abortedAgg.data,
        totals: { totalKilos: nominalAgg.totalKilos, totalCiclos: nominalAgg.totalCiclos },
      };
    } catch (error) {
      console.error('Error al calcular participación por lavadora:', error);
      return { data: [], aborted: [], totals: { totalKilos: 0, totalCiclos: 0 } };
    }
  }
};
